import { Link } from 'react-router-dom';
import {
  ALL_DIMENSIONS,
  DIMENSION_DESCRIPTIONS,
  DIMENSION_LABELS,
} from '../types/risk';
import './MethodologyPage.css';

/**
 * Institutional methodology page. Static PT-BR content explaining the
 * 5 dimensions of the Índice de Atenção Legislativa (IAL), how the composite
 * score is computed, and what guarantees the analysis offers (and doesn't).
 *
 * This page is the public-facing reference for the new positioning. The
 * tone is technical and defensible — it's the document a lawyer or
 * compliance officer reads before recommending the product internally.
 */
const DIMENSION_DETAILS: Record<
  (typeof ALL_DIMENSIONS)[number],
  { weight: number; details: string }
> = {
  thematic_coherence: {
    weight: 0.25,
    details:
      'Mede quanto o conteúdo da ementa se mantém aderente ao objeto e título declarados. Indicadores incluem termos exclusivos da ementa que não constam do título, dispersão semântica e empilhamento de matérias heterogêneas.',
  },
  rights_impact: {
    weight: 0.25,
    details:
      'Avalia a magnitude da restrição, expansão ou criação de direitos individuais ou coletivos. Considera presença de termos relativos a direitos, garantias, penalidades e obrigações de fazer/não-fazer.',
  },
  legislative_transparency: {
    weight: 0.2,
    details:
      'Examina a clareza redacional, especificidade dos comandos normativos e ausência de delegação excessiva ou condicionalidades obscuras. Sinaliza casos em que a leitura técnica do texto pode ser dificultada.',
  },
  legal_risk: {
    weight: 0.2,
    details:
      'Aponta possíveis vícios formais e pontos sensíveis para futuro controle de constitucionalidade ou questionamento jurídico. Inclui cláusulas de vigência condicional, encadeamentos longos e dispositivos com potencial de litigiosidade.',
  },
  regulatory_complexity: {
    weight: 0.1,
    details:
      'Estima a carga de obrigações criadas, prazos de adaptação e custo de compliance para destinatários. Indicadores incluem termos típicos de regulamentação, fiscalização, cadastro e procedimentos formais.',
  },
};

export default function MethodologyPage() {
  return (
    <main className="methodology-page">
      <header className="methodology-hero">
        <span className="methodology-eyebrow">Metodologia</span>
        <h1>Índice de Atenção Legislativa (IAL)</h1>
        <p className="methodology-lede">
          O IAL é um indicador composto que decompõe uma proposição legislativa
          em cinco dimensões independentes de análise. Cada dimensão recebe um
          score de 0 a 100 com indicadores textuais rastreáveis. O índice
          composto é a média ponderada dos cinco eixos.
        </p>
        <p className="methodology-disclaimer">
          Esta é uma análise preliminar automatizada baseada em heurísticas
          semânticas. Não substitui parecer jurídico nem juízo de mérito sobre
          o conteúdo das proposições.
        </p>
      </header>

      <section className="methodology-section">
        <h2>As cinco dimensões</h2>
        <p>
          Cada dimensão é avaliada de forma independente: uma proposição pode
          apresentar sinais altos em uma dimensão e baixos em outra. Os pesos
          do índice composto refletem a ênfase relativa de cada eixo na
          composição do IAL.
        </p>
        <div className="methodology-dimensions">
          {ALL_DIMENSIONS.map((id) => (
            <article key={id} className="methodology-dimension">
              <header>
                <h3>{DIMENSION_LABELS[id]}</h3>
                <span className="methodology-dimension-weight">
                  Peso no IAL: {Math.round(DIMENSION_DETAILS[id].weight * 100)}%
                </span>
              </header>
              <p className="methodology-dimension-short">
                {DIMENSION_DESCRIPTIONS[id]}
              </p>
              <p>{DIMENSION_DETAILS[id].details}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="methodology-section">
        <h2>Composição do índice</h2>
        <p>
          O score composto é uma média ponderada das cinco dimensões:
        </p>
        <pre className="methodology-formula">
{`IAL = 0.25 × aderência temática
    + 0.25 × impacto em direitos
    + 0.20 × transparência legislativa
    + 0.20 × risco jurídico
    + 0.10 × complexidade regulatória`}
        </pre>
        <p>
          A categorização em níveis segue o mesmo corte aplicado a cada
          dimensão individual: <strong>Baixo</strong> quando o score é inferior
          a 34, <strong>Médio</strong> entre 34 e 66, e <strong>Alto</strong> a
          partir de 67.
        </p>
      </section>

      <section className="methodology-section">
        <h2>Linguagem e responsabilidade</h2>
        <p>
          A análise utiliza exclusivamente linguagem de hipótese técnica
          (&ldquo;apresenta indícios de&rdquo;, &ldquo;pode suscitar&rdquo;,
          &ldquo;sugere&rdquo;). Não há afirmações absolutas sobre
          constitucionalidade, legalidade ou conformidade — esses juízos
          dependem de exame técnico-jurídico que esta ferramenta não
          substitui.
        </p>
        <p>
          O sistema rejeita ativamente afirmações categóricas geradas pelo
          modelo de linguagem. Quando detectadas, são suavizadas por
          pós-processamento ou descartadas via reanálise.
        </p>
      </section>

      <section className="methodology-section">
        <h2>Fontes e limitações</h2>
        <ul className="methodology-limitations">
          <li>
            A análise é realizada sobre o <strong>título e a ementa</strong>{' '}
            disponibilizados pelas casas legislativas. O texto integral da
            proposição não é avaliado.
          </li>
          <li>
            As dimensões refletem sinais textuais; <strong>não capturam</strong>{' '}
            contexto político, articulação parlamentar, urgência regimental ou
            negociação extra-textual.
          </li>
          <li>
            O índice é <strong>preliminar</strong>: serve como triagem para
            análise técnica aprofundada, não como conclusão final.
          </li>
          <li>
            Os dados-fonte são públicos e provêm das APIs oficiais da Câmara
            dos Deputados e do Senado Federal.
          </li>
        </ul>
      </section>

      <footer className="methodology-footer">
        <Link to="/dashboard" className="methodology-back-link">
          ← Voltar ao painel
        </Link>
      </footer>
    </main>
  );
}
